import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import type { SupplyPosition, DemandPosition, HedgePosition } from '../../../sp-types/positions';
import { calculatePriceInsightData, PriceInsightBucket, FxConverter, TimeGranularity } from './StrategiesUtils';
import { useCategoryTerms } from '../../../utils/categoryTerms';
import { useFx } from '../../../hooks/useFx';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { TimeGranularityToggle } from '../../../components/TimeGranularityToggle';
import { SpotSparkline } from './SpotSparkline';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend);

interface Props {
  formulaId: string;
  materialName: string;
  supplyPositions: SupplyPosition[];
  demandPositions: DemandPosition[];
  hedgePositions: HedgePosition[];
  spotPrice: number;
  terminals?: number[] | null;
  onBarSelect?: (_index: number) => void;
  selectedIndex?: number;
  timeGranularity?: TimeGranularity;
  onTimeChange?: (_time: TimeGranularity) => void;
  /** Cost basis drift alert threshold (%) — used to color the advantage badge */
  driftThreshold?: number;
}

const fmtQty = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtPrice = (v: number) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const HedgePriceManagement: React.FC<Props> = ({
  formulaId,
  materialName,
  supplyPositions,
  demandPositions,
  hedgePositions,
  spotPrice,
  terminals,
  onBarSelect,
  selectedIndex,
  timeGranularity: externalGranularity,
  onTimeChange,
  driftThreshold,
}) => {
  const [internalGranularity, setInternalGranularity] = useState<TimeGranularity>('Yearly');
  const timeGranularity = externalGranularity ?? internalGranularity;
  const setTimeGranularity = onTimeChange ?? setInternalGranularity;
  const chartRef = useRef<any>(null);
  const terms = useCategoryTerms();
  const [animReady, setAnimReady] = useState(false);
  const baseCurrency = useBaseCurrency();
  const { convertToBase } = useFx(baseCurrency);

  // Build a plain FxConverter from the hook (amount, currency) => baseAmount
  const fxConverter: FxConverter = React.useCallback(
    (amount: number, currency: string) => {
      const { amountBase } = convertToBase(amount, currency);
      return amountBase ?? 0;
    },
    [convertToBase],
  );

  const terminalCount = terminals ? terminals.length : 0;

  // Compute data
  const buckets = useMemo(() =>
    calculatePriceInsightData(
      formulaId, supplyPositions, demandPositions, hedgePositions,
      spotPrice, timeGranularity, terminals || [], 12, fxConverter,
    ),
    [formulaId, supplyPositions, demandPositions, hedgePositions, spotPrice, timeGranularity, terminals, fxConverter],
  );

  // In full mode there's exactly one bucket — use it for KPI display
  const summary = buckets[0];

  // Chart key for remount
  const chartKey = useMemo(
    () => `${formulaId}-${buckets.length}-${timeGranularity}`,
    [formulaId, buckets.length, timeGranularity],
  );

  // Animation: zeros first, then real data
  useEffect(() => {
    setAnimReady(false);
    const id = requestAnimationFrame(() => setAnimReady(true));
    return () => cancelAnimationFrame(id);
  }, [chartKey]);

  // ── Build chart data (stacked bar: secured vs demand per bucket) ──
  const chartData = useMemo(() => {
    const labels = buckets.map(b => b.label);
    const zero = () => buckets.map(() => 0);

    return {
      labels,
      datasets: [
        {
          label: 'On-Hand',
          data: animReady ? buckets.map(b => b.onHandQty || null) : zero(),
          backgroundColor: '#10B981',
          borderWidth: 0,
          borderRadius: 4,
          stack: 'secured',
        },
        {
          label: 'Supply POs',
          data: animReady ? buckets.map(b => b.supplyQty || null) : zero(),
          backgroundColor: '#3B82F6',
          borderWidth: 0,
          borderRadius: 4,
          stack: 'secured',
        },
        {
          label: terms.hedge,
          data: animReady ? buckets.map(b => b.hedgeQty || null) : zero(),
          backgroundColor: '#8B5CF6',
          borderWidth: 0,
          borderRadius: 4,
          stack: 'secured',
        },
        {
          label: terms.demand,
          data: animReady ? buckets.map(b => b.demandQty || null) : zero(),
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          borderColor: '#EF4444',
          borderWidth: 2,
          borderRadius: 4,
          stack: 'demand',
        },
      ],
    };
  }, [buckets, animReady]);

  // ── Chart options ──
  const options: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: buckets.length > 1 ? 28 : 0 } },
    animation: { duration: 400 },
    indexAxis: buckets.length === 1 ? 'y' as const : 'x' as const,
    interaction: { mode: 'index' as const, intersect: false },
    onClick: (_e: any, elements: any[]) => {
      if (elements.length > 0 && onBarSelect) onBarSelect(elements[0].index);
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: buckets.length === 1 },
        ticks: { font: { size: 11 } },
        ...(buckets.length === 1 && {
          title: { display: true, text: 'Quantity (MT)', font: { size: 11 } },
          ticks: {
            font: { size: 10 },
            callback: (v: any) => {
              const num = Number(v);
              return num >= 1000 ? `${(num / 1000).toFixed(0)}k` : String(num);
            },
          },
        }),
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: {
          font: { size: 10 },
          callback: (v: any) => {
            const num = Number(v);
            if (buckets.length === 1) return String(v); // category labels
            return num >= 1000 ? `${(num / 1000).toFixed(0)}k` : num.toLocaleString();
          },
        },
        ...(buckets.length > 1 && {
          title: { display: true, text: 'Quantity (MT)', font: { size: 11 } },
        }),
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true, pointStyle: 'rect', padding: 12, font: { size: 10 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed[buckets.length === 1 ? 'x' : 'y'];
            if (val == null) return '';
            return ` ${ctx.dataset.label}: ${fmtQty(val)} MT`;
          },
          afterBody: (items: any[]) => {
            if (!items.length) return '';
            const b = buckets[items[0].dataIndex];
            if (!b || !b.weightedAvgCost) return '';
            const lines = [
              '',
              `Avg Cost: ${fmtPrice(b.weightedAvgCost)}/MT`,
              `Spot:     ${fmtPrice(b.spotPrice)}/MT`,
            ];
            const adv = b.priceAdvantage;
            lines.push(`${adv > 0 ? '▲' : '▼'} ${Math.abs(adv).toFixed(1)}% ${adv > 0 ? 'below' : 'above'} spot`);
            return lines.join('\n');
          },
        },
      },
    },
  }), [buckets, onBarSelect]);

  // ── Price label plugin (multi-bucket: avg cost + advantage % above each bar) ──
  const priceAdvPlugin = useMemo(() => ({
    id: 'priceInsightAdvLabel',
    afterDraw(chart: any) {
      if (buckets.length <= 1) return;
      const ctx = chart.ctx;
      const datasetCount = chart.data.datasets.length;
      ctx.save();
      ctx.textAlign = 'center';
      buckets.forEach((b, i) => {
        if (!b.weightedAvgCost) return;
        // Find the highest bar top across the SECURED stack only (skip demand dataset)
        let minY = chart.chartArea.bottom;
        for (let ds = 0; ds < datasetCount; ds++) {
          if (chart.data.datasets[ds]?.stack === 'demand') continue;
          const meta = chart.getDatasetMeta(ds);
          const bar = meta?.data?.[i];
          if (bar && bar.y < minY) minY = bar.y;
        }
        const firstBar = chart.getDatasetMeta(0)?.data?.[i];
        if (!firstBar) return;

        // Avg cost label
        ctx.font = 'bold 10px sans-serif';
        ctx.fillStyle = '#374151';
        ctx.fillText(
          `$${b.weightedAvgCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
          firstBar.x, minY - 18,
        );

        // Price advantage % below cost
        if (b.priceAdvantage !== 0) {
          const adv = b.priceAdvantage;
          ctx.font = 'bold 9px sans-serif';
          ctx.fillStyle = adv > 0 ? '#059669' : '#DC2626';
          ctx.fillText(
            `${adv > 0 ? '+' : ''}${adv.toFixed(0)}%`,
            firstBar.x, minY - 7,
          );
        }
      });
      ctx.restore();
    },
  }), [buckets]);

  const plugins = useMemo(() => [priceAdvPlugin], [priceAdvPlugin]);

  // ── KPI Cards (shown in full-horizon mode) ──
  const renderKPIs = () => {
    if (!summary) return null;
    const adv = summary.priceAdvantage;
    const isFavorable = adv > 0;
    const covPct = summary.coverageRatio * 100;

    return (
      <div className="grid grid-cols-3 gap-3 mb-3 overflow-visible">
        {/* Weighted Avg Cost */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Avg Cost</div>
          <div className="text-xl font-bold text-gray-900 tabular-nums">
            {summary.weightedAvgCost > 0 ? fmtPrice(summary.weightedAvgCost) : '—'}
            <span className="text-xs font-normal text-gray-400">/MT</span>
          </div>
          {summary.weightedAvgCost > 0 && (() => {
            const absAdv = Math.abs(adv);
            const threshold = driftThreshold || 10;
            const isAlert = absAdv >= threshold;
            const colorClass = isFavorable
              ? 'text-green-600'
              : isAlert ? 'text-red-600' : 'text-amber-600';
            return (
              <div className={`text-xs font-semibold mt-1 ${colorClass}`}>
                {isFavorable ? '▲' : '▼'} {absAdv.toFixed(1)}% {isFavorable ? 'below' : 'above'} spot
                {isAlert && !isFavorable && (
                  <span className="ml-1 px-1 py-0.5 text-[9px] rounded bg-red-100 text-red-700">⚠ ≥{threshold}%</span>
                )}
              </div>
            );
          })()}
        </div>

        {/* Spot Price */}
        <div className="bg-gray-50 rounded-lg p-3 overflow-visible">
          <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Spot Price</div>
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-xl font-bold text-gray-500 tabular-nums">
              {fmtPrice(summary.spotPrice)}
            </span>
            <span className="text-xs font-normal text-gray-400">/MT</span>
            <SpotSparkline formulaCode={formulaId} inline />
          </div>
          <div className="overflow-visible relative">
            <SpotSparkline formulaCode={formulaId} height={36} />
          </div>
        </div>

        {/* Coverage */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Coverage</div>
          <div className={`text-xl font-bold tabular-nums ${covPct >= 100 ? 'text-green-600' : covPct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {covPct > 0 ? `${covPct.toFixed(0)}%` : '—'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {fmtQty(summary.totalSecured)} vs {fmtQty(summary.demandQty)} MT
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col bg-white shadow-md rounded-lg p-3 h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Price Insight</h2>
          <p className="text-[10px] text-gray-500 mt-0.5">
            Weighted avg cost of all secured stock vs spot — {materialName}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <TimeGranularityToggle
            value={timeGranularity}
            onChange={setTimeGranularity}
            options={terminalCount > 0 && terminalCount < 12
              ? ['Month', 'Quarter', 'Terminal', 'Yearly', 'Full']
              : ['Month', 'Quarter', 'Yearly', 'Full']
            }
          />
        </div>
      </div>

      {/* KPI cards (full horizon mode) */}
      {buckets.length <= 1 && renderKPIs()}

      {/* Chart */}
      <div className="flex-1 overflow-hidden" style={{ minHeight: buckets.length <= 1 ? 120 : 200 }}>
        <Chart
          ref={chartRef}
          key={chartKey}
          type="bar"
          data={chartData as any}
          options={options as any}
          plugins={plugins}
        />
      </div>
    </div>
  );
};
